import { isSameDay, startOfDay, startOfTomorrow } from 'date-fns';
import { useEffect, useState } from 'react';

/**
 * The start of the current day, updated at midnight and when the app becomes
 * visible again (timers do not run while the device sleeps). Use it to work out
 * relative periods such as "today", so a view left open past midnight moves on
 * to the new day instead of showing the previous one.
 */
export const useCurrentDay = (): Date => {
  const [day, setDay] = useState(() => startOfDay(new Date()));

  useEffect(() => {
    const update = () => {
      const now = new Date();
      // Same instance on the same day, so nothing re-renders.
      setDay((current) =>
        isSameDay(current, now) ? current : startOfDay(now),
      );
    };

    let timer: ReturnType<typeof setTimeout>;
    const scheduleNextMidnight = () => {
      timer = setTimeout(() => {
        update();
        scheduleNextMidnight();
      }, startOfTomorrow().getTime() - Date.now());
    };

    scheduleNextMidnight();
    document.addEventListener('visibilitychange', update);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', update);
    };
  }, []);

  return day;
};
